import * as React from 'react';
import find from 'lodash/find';
import orderBy from 'lodash/orderBy';
import sortBy from 'lodash/sortBy';
import { Modal, Row, Col, Select, InputNumber, message } from "antd";
import { moveTrees } from "../../Repository/TreeRepository";
import { MapboxGeoJSONFeature } from "mapbox-gl";
import { Contract } from "tree-wallet-client";
import { GlobalContext } from '../../Contexts/GlobalContext';
import {
  MOVE_TREES_FAILED,
  NO_CONTRACT_SELECTED,
  NO_TREES_MOVED,
  NO_TREES_SELECTED,
  TOO_MANY_TREES_SELECTED,
  treesMoved,
} from '../../Helper/BannerMessages';

interface MoveTreesPopupProps {
  open: boolean;
  onClose: Function;
  onFinish: Function;
  featureStates: MapboxGeoJSONFeature[];
  features: MapboxGeoJSONFeature[];
}

export default function MoveTreesPopup(props: MoveTreesPopupProps) {
  const { features, open: defaultOpen, onClose } = props;

  const nbTrees = {
    holes: features.length,
    planted: features.reduce((acc, feature) => {
      return acc + parseInt(String(feature.properties?.numberPlanted));
    }, 0)
  }

  const [open, setOpen] = React.useState(defaultOpen);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [contract, setContract] = React.useState<Contract|undefined>(undefined);
  const [numberOfTrees, setNumberOfTrees] = React.useState<number>(nbTrees.planted);
  let { contracts: contractsList } = React.useContext(GlobalContext);
  contractsList = sortBy(contractsList, ['description']);

  if (!features || features.length === 0) {
    message.info(NO_TREES_SELECTED);
    return null;
  }

  const hideModal = () => {
    setOpen(false);
    onClose();
  }

  const sortTrees = (features: MapboxGeoJSONFeature[]) => {
    const { featureStates } = props;
    const sortedFeatures: any = {};
    const orderedFeatures: any = orderBy(features, ['properties.id'], ['desc']);

    for (const feature of orderedFeatures) {
      const featureState = find(featureStates, (current) => {
        // @ts-ignore
        return current.properties && current.properties.id === feature.properties?.id;
      });
      if (featureState) {
        feature.properties = {
          ...feature.properties,
          // @ts-ignore
          contractId: featureState.properties.contractId,
        };
      }

      const contractId = feature.properties?.contractId;
      if (!sortedFeatures[contractId]) {
        sortedFeatures[contractId] = [];
      }
      sortedFeatures[contractId].push(feature);
    }
    return sortedFeatures;
  };

  const filterFeatures = (features: MapboxGeoJSONFeature[]) => {
    let counter = 0;
    const filteredFeatures = features.filter((feature: MapboxGeoJSONFeature) => {
      if (counter < numberOfTrees) {
        counter += parseInt(String(feature.properties?.numberPlanted));
        return true;
      }
      return false;
    });

    return {
      counter,
      filteredFeatures,
      ids: filteredFeatures.map((feature: MapboxGeoJSONFeature) => {
        return parseInt(String(feature.properties?.id));
      }),
    };
  }

  const onOk = async () => {
    const { onFinish } = props;

    if (numberOfTrees > nbTrees.planted) {
      message.error(TOO_MANY_TREES_SELECTED);
      return;
    }

    if (numberOfTrees === 0) {
      message.info(NO_TREES_MOVED);
      return;
    }

    if (!contract) {
      message.info(NO_CONTRACT_SELECTED);
      return;
    }

    try {
      const { filteredFeatures, ids, counter } = filterFeatures(features);
      setLoading(true);
      await moveTrees(
        ids,
        numberOfTrees,
        contract.id,
      );
      setLoading(false);
      hideModal();
      onFinish(filteredFeatures, contract.id);
      message.info(treesMoved(counter, contract.description))
    } catch (e) {
      message.error(MOVE_TREES_FAILED);
      console.error(e);
    }
  };

  const paragraphStyle = { paddingBottom: '25px' };
  const subtitleStyle = { paddingBottom: '10px' };
  const options = contractsList && contractsList.map((contract) => {
    return {
      label: contract.description,
      value: contract.id
    };
  });

  const sortedFeatures = sortTrees(features);

  return (
    <div>
      <Modal
        open={open}
        closable={true}
        onCancel={hideModal}
        maskClosable={false}
        title={'Move trees'}
        onOk={onOk}
        cancelButtonProps={{ style: { display: 'none' } }}
        okButtonProps={{ loading: loading }}
      >
        <Row>
          <Col span={24}>
            <h3>{nbTrees.planted} trees selected (in {nbTrees.holes} holes)</h3>
            <div style={paragraphStyle}>
              <ul>
                {sortedFeatures && Object.entries(sortedFeatures).map(([contractId, featuresArray]: [string, MapboxGeoJSONFeature[]]) => {
                  const numberOfTrees = featuresArray.reduce((acc: number, feature: MapboxGeoJSONFeature) => {
                    return acc + parseInt(String(feature.properties?.numberPlanted));
                  }, 0);
                  const contract = find(contractsList, { id: parseInt(contractId) });
                  // @ts-ignore
                  return <li key={'contract_description_' + contract?.id}>
                    <strong>{numberOfTrees}</strong> trees on <strong>{contract?.description}</strong>
                  </li>;
                })}
              </ul>
            </div>
            <div style={paragraphStyle}>
              <div style={subtitleStyle}>
                Please select how many you want to move
              </div>
              <div>
                <InputNumber
                  min={1}
                  max={nbTrees.planted}
                  placeholder={'Type in the number of trees you want to move'}
                  defaultValue={nbTrees.planted}
                  onChange={(value: number) => setNumberOfTrees(value)}
                />
              </div>
            </div>
            <div style={paragraphStyle}>
              <div style={subtitleStyle}>
                Please select a contract to move the trees to
              </div>
              <div>
                <Select
                  onChange={(value) => setContract(find(contractsList, { id: value }))}
                  loading={options && options.length === 0}
                  options={options}
                  style={{ width: '200px' }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Modal>
    </div>
  );
}